// src/utils/toneMigration.js
// ─── Tone migration helpers ──────────────────────────────────────────────
// Centralizes FE state ↔ backend `tone_hint` conversion + legacy fallback.
//
// Backend semantics:
//   - tone_hint: "brand-voice"  → use brand_guidelines.voice verbatim
//   - tone_hint: null / omitted → blend persona + objective + brand voice
//   - any other string          → free-form override
//
// Legacy shape (pre-refactor entries still in-memory this session):
//   tone_overrides: { style: X }  or  tone_overrides: { custom: X }

import { TONE_OPTIONS } from '../constants/options.js';

const KNOWN_TONE_VALUES = new Set(
  TONE_OPTIONS.filter(o => o.value !== 'custom').map(o => o.value)
);

// Label → slug map (case-insensitive). Lets us recognize examples whose
// tone_hint was authored as the human label ("Bold & Provocative") instead
// of the canonical slug ("bold-provocative").
const LABEL_TO_VALUE = new Map(
  TONE_OPTIONS
    .filter(o => o.value !== 'custom')
    .map(o => [o.label.toLowerCase(), o.value])
);

export function resolveToneHint(state) {
  if (!state) return null;
  if (state.tone === 'custom') {
    const trimmed = (state.customTone || '').trim();
    return trimmed || null;
  }
  return state.tone || null;
}

export function toneStateFromParams(params) {
  if (!params) return {};
  const hint =
    params.tone_hint ??
    params.tone_overrides?.style ??
    params.tone_overrides?.custom ??
    null;
  if (!hint) return {};
  if (KNOWN_TONE_VALUES.has(hint)) return { tone: hint, customTone: '' };
  if (typeof hint === 'string') {
    const slugFromLabel = LABEL_TO_VALUE.get(hint.toLowerCase());
    if (slugFromLabel) return { tone: slugFromLabel, customTone: '' };
  }
  return { tone: 'custom', customTone: hint };
}

export function displayToneFromParams(params) {
  if (!params) return null;
  return (
    params.tone_hint ??
    params.tone_overrides?.style ??
    params.tone_overrides?.custom ??
    null
  );
}
